<template>
  <AdminPageView :loading :loadingErrorMessage :actionsOnlyOnTop="true" title="Utilisateurs">
    <template #actions>
      <router-link :to="{ name: 'admin-new-participant' }" class="btn btn-outline-success">
        <i class="fa-solid fa-plus pe-2"></i> Ajouter un utilisateur
      </router-link>
    </template>

    <!-- Barre de contrôles -->
    <div class="d-flex flex-wrap gap-2 align-items-center mt-3 mb-2">
      <!-- Recherche texte -->
      <div class="input-group" style="max-width: 350px;">
        <span class="input-group-text"><i class="fa-solid fa-magnifying-glass"></i></span>
        <input
          v-model="searchInput"
          type="text"
          class="form-control"
          placeholder="Rechercher (nom, email, entreprise)..."
        />
        <button v-if="searchInput" class="btn btn-outline-secondary" @click="searchInput = ''">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>

      <!-- Sélecteur de taille de page -->
      <select v-model="pageSize" class="form-select" style="max-width: 140px;">
        <option :value="10">10 / page</option>
        <option :value="25">25 / page</option>
        <option :value="50">50 / page</option>
        <option :value="100">100 / page</option>
      </select>

      <!-- Compteur résultats -->
      <span class="text-muted ms-auto small">
        {{ totalItems }} utilisateur{{ totalItems !== 1 ? 's' : '' }}
      </span>
    </div>

    <div class="table-responsive">
      <table class="table table-striped">
        <thead>
          <tr>
            <th
              scope="col"
              class="sortable-col"
              :class="{ 'sort-active': sortBy === 'civility' }"
              @click="toggleSort('civility')"
            >
              Civilité <SortIconComponent column="civility" :sortColumn="sortBy" :sortDirection />
            </th>
            <th
              scope="col"
              class="sortable-col"
              :class="{ 'sort-active': sortBy === 'lastName' }"
              @click="toggleSort('lastName')"
            >
              Nom <SortIconComponent column="lastName" :sortColumn="sortBy" :sortDirection />
            </th>
            <th
              scope="col"
              class="sortable-col"
              :class="{ 'sort-active': sortBy === 'firstName' }"
              @click="toggleSort('firstName')"
            >
              Prénom <SortIconComponent column="firstName" :sortColumn="sortBy" :sortDirection />
            </th>
            <th
              scope="col"
              class="sortable-col"
              :class="{ 'sort-active': sortBy === 'email' }"
              @click="toggleSort('email')"
            >
              Email <SortIconComponent column="email" :sortColumn="sortBy" :sortDirection />
            </th>
            <th
              scope="col"
              class="sortable-col"
              :class="{ 'sort-active': sortBy === 'company' }"
              @click="toggleSort('company')"
            >
              Entreprise <SortIconComponent column="company" :sortColumn="sortBy" :sortDirection />
            </th>
            <th
              scope="col"
              class="sortable-col"
              :class="{ 'sort-active': sortBy === 'status' }"
              @click="toggleSort('status')"
            >
              Statut <SortIconComponent column="status" :sortColumn="sortBy" :sortDirection />
            </th>
            <th
              scope="col"
              class="sortable-col"
              :class="{ 'sort-active': sortBy === 'isAdmin' }"
              @click="toggleSort('isAdmin')"
            >
              Admin <SortIconComponent column="isAdmin" :sortColumn="sortBy" :sortDirection />
            </th>
            <th scope="col">Evénements</th>
            <th scope="col" class="text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          <template v-if="users.length > 0">
            <tr v-for="user in users" :key="user.id">
              <td>{{ GetCivilityShortLabel(user.civility) }}</td>
              <td class="text-uppercase">{{ user.lastName?.toUpperCase() }}</td>
              <td class="text-capitalize">{{ user.firstName }}</td>
              <td>{{ user.email }}</td>
              <td>{{ user.company }}</td>
              <td>{{ user.status ? GetUserStatusLabel(user.status) : '' }}</td>
              <td class="ps-4">
                <i v-if="user.isAdmin" class="fa-solid fa-check text-success"></i>
              </td>
              <td class="ps-4">
                <router-link
                  v-if="user.participationsBasic && user.participationsBasic.length > 0"
                  :to="{ name: 'admin-edit-participant', params: { id: user.id } }"
                  class="badge bg-primary text-decoration-none"
                  title="Voir les participations de l'utilisateur"
                >
                  {{ user.participationsBasic.length }}
                </router-link>
                <span v-else class="badge bg-secondary">0</span>
              </td>
              <td class="py-0 text-center text-nowrap">
                <router-link
                  :to="{ name: 'admin-edit-participant', params: { id: user.id } }"
                  class="btn btn-link px-2"
                  title="Modifier le profil de l'utilisateur"
                >
                  <i class="fa-solid fa-user-pen"></i>
                </router-link>
                <button class="btn btn-link px-2" @click="deleteUser(user)" title="Supprimer l'utilisateur">
                  <i class="fa-regular fa-trash-can"></i>
                </button>
              </td>
            </tr>
          </template>
          <tr v-else>
            <td colspan="9" class="text-center text-muted py-4">
              <template v-if="search">
                <i class="fa-solid fa-filter me-2"></i>Aucun utilisateur ne correspond à la recherche.
              </template>
              <template v-else>
                Aucun utilisateur trouvé.
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <nav v-if="totalPages > 1" class="d-flex justify-content-center mt-2">
      <ul class="pagination">
        <li class="page-item" :class="{ disabled: page === 0 }">
          <button class="page-link" @click="goToPage(0)">
            <i class="fa-solid fa-angles-left"></i>
          </button>
        </li>
        <li class="page-item" :class="{ disabled: page === 0 }">
          <button class="page-link" @click="goToPage(page - 1)">
            <i class="fa-solid fa-angle-left"></i>
          </button>
        </li>
        <li
          v-for="p in visiblePages"
          :key="p"
          class="page-item"
          :class="{ active: p === page }"
        >
          <button class="page-link" @click="goToPage(p)">{{ p + 1 }}</button>
        </li>
        <li class="page-item" :class="{ disabled: page >= totalPages - 1 }">
          <button class="page-link" @click="goToPage(page + 1)">
            <i class="fa-solid fa-angle-right"></i>
          </button>
        </li>
        <li class="page-item" :class="{ disabled: page >= totalPages - 1 }">
          <button class="page-link" @click="goToPage(totalPages - 1)">
            <i class="fa-solid fa-angles-right"></i>
          </button>
        </li>
      </ul>
    </nav>
  </AdminPageView>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { GetCivilityShortLabel, GetUserStatusLabel, useConfirmDialog } from '@/compositions/helper';

import adminApi from '@/api/adminApi';
import AdminPageView from '@/views/admin/AdminPageView.vue';
import SortIconComponent from '@/components/common/SortIconComponent.vue';
import { useNotificationsStore } from '@/stores/notificationStore';

// ─── Types ───────────────────────────────────────────────────────────────────

type SortDirection = 'asc' | 'desc';

interface UsersPreferences {
  pageSize: number;
  sortBy: string | null;
  sortDirection: SortDirection;
}

// ─── Constantes ──────────────────────────────────────────────────────────────

const LS_KEY = 'mynemo_admin-users-preferences';

const DEFAULT_PREFERENCES: UsersPreferences = {
  pageSize: 25,
  sortBy: null,
  sortDirection: 'asc',
};

// ─── Stores / composables ─────────────────────────────────────────────────────

const notificationsStore = useNotificationsStore();
const { confirmDialog } = useConfirmDialog();

// ─── State ────────────────────────────────────────────────────────────────────

const loading = ref(true);
const loadingErrorMessage = ref<string | undefined>(undefined);

const users = ref<AdminUser[]>([]);
const totalItems = ref(0);
const page = ref(0);
const pageSize = ref(DEFAULT_PREFERENCES.pageSize);
const search = ref('');
const searchInput = ref('');
const sortBy = ref<string | null>(DEFAULT_PREFERENCES.sortBy);
const sortDirection = ref<SortDirection>(DEFAULT_PREFERENCES.sortDirection);

let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

// ─── Computed ─────────────────────────────────────────────────────────────────

const totalPages = computed(() => {
  return Math.max(1, Math.ceil(totalItems.value / pageSize.value));
});

const visiblePages = computed(() => {
  const total = totalPages.value;
  const current = page.value;
  const maxVisible = 5;

  if (total <= maxVisible) {
    return Array.from({ length: total }, (_, i) => i);
  }

  let start = Math.max(0, current - Math.floor(maxVisible / 2));
  let end = start + maxVisible;

  if (end > total) {
    end = total;
    start = end - maxVisible;
  }

  return Array.from({ length: end - start }, (_, i) => start + i);
});

// ─── localStorage ─────────────────────────────────────────────────────────────

const loadPreferences = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      const prefs: UsersPreferences = JSON.parse(raw);
      if (typeof prefs.pageSize === 'number' && prefs.pageSize > 0) {
        pageSize.value = prefs.pageSize;
      }
      if (prefs.sortBy !== undefined) {
        sortBy.value = prefs.sortBy;
      }
      if (prefs.sortDirection === 'asc' || prefs.sortDirection === 'desc') {
        sortDirection.value = prefs.sortDirection;
      }
    }
  } catch {
    // localStorage indisponible ou JSON invalide — on ignore
  }
};

const savePreferences = () => {
  try {
    const prefs: UsersPreferences = {
      pageSize: pageSize.value,
      sortBy: sortBy.value,
      sortDirection: sortDirection.value,
    };
    localStorage.setItem(LS_KEY, JSON.stringify(prefs));
  } catch {
    // localStorage indisponible — on ignore
  }
};

// ─── Actions tri ──────────────────────────────────────────────────────────────

const toggleSort = (col: string) => {
  if (sortBy.value === col) {
    if (sortDirection.value === 'asc') {
      sortDirection.value = 'desc';
    } else {
      sortBy.value = null;
      sortDirection.value = 'asc';
    }
  } else {
    sortBy.value = col;
    sortDirection.value = 'asc';
  }
  savePreferences();
  page.value = 0;
  loadUsers();
};

// ─── Navigation pages ─────────────────────────────────────────────────────────

const goToPage = (p: number) => {
  if (p < 0 || p >= totalPages.value) return;
  page.value = p;
  loadUsers();
};

// ─── Chargement données ───────────────────────────────────────────────────────

const loadUsers = () => {
  adminApi.getUsers({
    page: page.value,
    pageSize: pageSize.value,
    search: search.value || undefined,
    sortBy: sortBy.value || undefined,
    sortDirection: sortBy.value ? sortDirection.value : undefined,
  }).then((response) => {
    users.value = response.data.items as AdminUser[];
    totalItems.value = response.data.totalItems;
  }).catch((error: Error) => {
    if (loading.value) {
      loadingErrorMessage.value = 'Impossible de charger les utilisateurs';
    } else {
      notificationsStore.notifyError(
        'Erreur lors du chargement des utilisateurs',
        error.message
      );
    }
    console.error('Error loading users:', error);
  }).finally(() => {
    loading.value = false;
  });
};

// ─── Suppression ──────────────────────────────────────────────────────────────

const deleteUser = async (user: AdminUser) => {
  if (
    await confirmDialog(
      `Êtes-vous sûr de vouloir supprimer l'utilisateur ${user.firstName} ${user.lastName?.toUpperCase()} (${user.email}) ?`
    )
  ) {
    adminApi.deleteUser(user.id).then(() => {
      notificationsStore.notifySuccess(
        'Utilisateur supprimé',
        'L\'utilisateur a été supprimé avec succès.'
      );
      loadUsers();
    }).catch((error: Error) => {
      notificationsStore.notifyError(
        'Erreur lors de la suppression de l\'utilisateur.',
        error.message
      );
    });
  }
};

// ─── Watchers ─────────────────────────────────────────────────────────────────

watch(searchInput, (val) => {
  if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
  searchDebounceTimer = setTimeout(() => {
    search.value = val.trim();
    page.value = 0;
    loadUsers();
  }, 200);
});

watch(pageSize, () => {
  page.value = 0;
  savePreferences();
  loadUsers();
});

// ─── Lifecycle ────────────────────────────────────────────────────────────────

onMounted(() => {
  loadPreferences();
  loadUsers();
});
</script>
